import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

import { useOutsideClick } from '@/hooks/ui/useOutsideClick';
import { FloatingPanel } from '@/shared/ui/FloatingPanel';
import type { PersonItem } from '../../types';
import { onAltArrowDownOpen } from '../../utils';
import { RChevron, RSearch, RTick } from './icons';
import { RPersonAvatar } from './RPersonAvatar';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ANY = any;

export type RoleDepartment = {
  id: number;
  name: string;
  memberIds: number[];
};

interface Props {
  label: string;
  items: PersonItem[];
  selectedIds: number[];
  /** Выбор человека: для «Исполнителя»/«Постановщика» — замена, иначе тумблер. */
  onPick: (id: number) => void;
  /** Снятие чипа. Не передан — крестика у чипа нет (роль обязательна). */
  onRemove?: (id: number) => void;
  /** Отделы для «Добавить всех» — только у ролей со множественным выбором. */
  departments?: RoleDepartment[];
  /** Добавить сразу несколько человек (без дублей — считает вызывающий). */
  onAddMany?: (ids: number[]) => void;
  multiple?: boolean;
  disabled?: boolean;
}

/** Роль участника из макета: подпись с шевроном, «Добавить всех» справа,
 *  выпадашка со списком людей и строка чипов под ней. */
export function RRolePicker({
  label,
  items,
  selectedIds,
  onPick,
  onRemove,
  departments,
  onAddMany,
  multiple = false,
  disabled,
}: Props) {
  const [open, setOpen] = useState(false);
  const [deptOpen, setDeptOpen] = useState(false);
  const [q, setQ] = useState('');
  const headRef = useRef<HTMLSpanElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const deptBtnRef = useRef<HTMLButtonElement | null>(null);
  const deptPanelRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useOutsideClick({
    enabled: open,
    refs: [btnRef as ANY, panelRef as ANY],
    onOutside: () => setOpen(false),
  });

  useOutsideClick({
    enabled: deptOpen,
    refs: [deptBtnRef as ANY, deptPanelRef as ANY],
    onOutside: () => setDeptOpen(false),
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    setQ('');
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) {
      return items;
    }
    return items.filter((u) =>
      String(u.name ?? '')
        .toLowerCase()
        .includes(s)
    );
  }, [items, q]);

  const picked = useMemo<PersonItem[]>(() => {
    const byId = new Map(items.map((u) => [u.id, u]));
    return selectedIds.map((id) => byId.get(id) ?? { id, name: `#${id}` });
  }, [items, selectedIds]);

  return (
    <div className="min-w-0">
      <span ref={headRef} className="relative flex items-center gap-[8px]">
        <button
          ref={btnRef}
          type="button"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          onKeyDown={onAltArrowDownOpen(open, () => setOpen(true))}
          className={clsx(
            'flex items-center gap-[6px] text-[12px] font-medium text-[rgba(20,30,49,0.6)]',
            disabled && 'cursor-not-allowed opacity-50'
          )}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          {label}
          <RChevron />
        </button>
        <span className="flex-1" />
        {onAddMany ? (
          <button
            ref={deptBtnRef}
            type="button"
            disabled={disabled}
            onClick={() => {
              setDeptOpen((v) => !v);
              setOpen(false);
            }}
            onKeyDown={onAltArrowDownOpen(deptOpen, () => setDeptOpen(true))}
            className={clsx(
              'whitespace-nowrap border-b border-[rgba(59,77,127,0.25)] pb-[3px] pt-[2px]',
              'text-[12px] font-medium text-[#3B4D7F] transition-colors',
              disabled
                ? 'cursor-not-allowed opacity-50'
                : 'hover:border-[#57B792] hover:text-[#57B792]'
            )}
          >
            Добавить всех
          </button>
        ) : null}

        {deptOpen && onAddMany ? (
          <FloatingPanel
            ref={deptPanelRef}
            anchorRef={headRef}
            align="end"
            topOffset={30}
            className="block max-h-[280px] w-[226px] overflow-y-auto rounded-[14px] bg-white p-[6px] shadow-[0_18px_40px_rgba(20,30,49,0.22)]"
          >
            <span className="flex items-center gap-[8px] px-[8px] pb-[8px] pt-[4px]">
              <span className="flex-1 text-[10px] font-bold tracking-[0.6px] text-[rgba(20,30,49,0.4)]">
                ОТДЕЛЫ
              </span>
              <button
                type="button"
                title="Закрыть"
                onClick={() => setDeptOpen(false)}
                className="flex h-[20px] w-[20px] items-center justify-center rounded-full"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="rgba(20,30,49,0.5)"
                  strokeWidth={2}
                  strokeLinecap="round"
                  aria-hidden
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </span>

            {(departments ?? []).length === 0 ? (
              <div className="px-[9px] py-[10px] text-[12px] text-[rgba(20,30,49,0.5)]">
                Отделов нет
              </div>
            ) : (
              (departments ?? []).map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => {
                    onAddMany(d.memberIds);
                    setDeptOpen(false);
                  }}
                  className="flex w-full items-center gap-[9px] rounded-[10px] px-[9px] py-[8px] text-left hover:bg-[rgba(59,77,127,0.06)]"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#3B4D7F"
                    strokeWidth={1.8}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="shrink-0"
                    aria-hidden
                  >
                    <path d="M9 8.6a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM2.8 19.4a6.2 6.2 0 0 1 12.4 0M16.6 5.4a3 3 0 0 1 0 5.8M18.6 19.4a6.4 6.4 0 0 0-2-4.6" />
                  </svg>
                  <span className="min-w-0 flex-1 text-[12px] font-semibold text-[#141E31]">
                    {d.name}
                  </span>
                  <span className="whitespace-nowrap text-[11px] text-[rgba(20,30,49,0.45)]">
                    {d.memberIds.length} чел.
                  </span>
                </button>
              ))
            )}
          </FloatingPanel>
        ) : null}

        {open ? (
          <FloatingPanel
            ref={panelRef}
            anchorRef={headRef}
            topOffset={30}
            className="block max-h-[280px] w-[250px] overflow-y-auto rounded-[14px] bg-white p-[6px] shadow-[0_18px_40px_rgba(20,30,49,0.22)]"
            role="menu"
          >
            <span className="mx-[2px] mb-[6px] mt-[2px] flex items-center gap-[8px] rounded-[10px] bg-[#F1F3F8] px-[10px] py-[7px]">
              <RSearch />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Поиск по имени"
                className="min-w-0 flex-1 border-none bg-transparent text-[11.5px] text-[#575757] outline-none"
              />
            </span>

            {filtered.length === 0 ? (
              <div className="px-[9px] py-[10px] text-[12px] text-[rgba(20,30,49,0.5)]">
                Ничего не найдено
              </div>
            ) : (
              filtered.map((u) => {
                const active = selectedIds.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => {
                      onPick(u.id);
                      if (!multiple) {
                        setOpen(false);
                      }
                    }}
                    className="flex w-full items-center gap-[9px] rounded-[10px] px-[9px] py-[8px] text-left hover:bg-[rgba(59,77,127,0.06)]"
                    style={{
                      background: active
                        ? 'rgba(59,77,127,0.05)'
                        : 'transparent',
                    }}
                    role="menuitem"
                    title={u.name}
                  >
                    <RPersonAvatar
                      id={u.id}
                      name={u.name}
                      avatarUrl={u.avatarUrl}
                    />
                    <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-[#141E31]">
                      {u.name}
                    </span>
                    {active ? (
                      <span className="flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-full bg-[#57B792]">
                        <RTick />
                      </span>
                    ) : null}
                  </button>
                );
              })
            )}
          </FloatingPanel>
        ) : null}
      </span>

      <span className="mt-[7px] flex flex-wrap gap-[6px]">
        {picked.map((u) => (
          <span
            key={u.id}
            className="inline-flex items-center gap-[7px] rounded-full bg-[#F1F3F8] py-[4px] pl-[4px] pr-[10px]"
          >
            <RPersonAvatar
              id={u.id}
              name={u.name}
              avatarUrl={u.avatarUrl}
              size={22}
              fontSize={9}
            />
            <span className="whitespace-nowrap text-[12px] font-medium text-[#575757]">
              {u.name}
            </span>
            {onRemove ? (
              <button
                type="button"
                disabled={disabled}
                onClick={() => onRemove(u.id)}
                className="text-[12px] text-[rgba(20,30,49,0.42)]"
                aria-label={`Убрать ${u.name}`}
              >
                ×
              </button>
            ) : null}
          </span>
        ))}
      </span>
    </div>
  );
}
